import { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import Chart from 'react-apexcharts';
import { RefreshCw, Wallet, ChevronRight, TrendingUp, X } from 'lucide-react';
import { change_Current_Location, changeHeaderTitle } from '../store';
import { useAgentMobilePageHeader } from '../layout/AgentMobilePageHeader';
import AgentWalletTransfer from './component/AgentWalletTransfer';
import useAgentEarnings from './agentEarnings/useAgentEarnings';
import { kstLabel, won } from './agentEarnings/data';
import { isEarningsDemo } from './agentEarnings/demo';
import { visibleTrend, weeklyTrend } from './agentEarnings/trend';
import MobileTrend from './agentEarnings/MobileTrendView';
import './agentEarnings/dashboard.css';

const TODAY = { period: 'today' };
const LABELS = { today: '오늘', week: '이번 주', month: '이번 달' };
function useMobileTrendLayout() {
    const matches = () => typeof window.matchMedia === 'function' ? window.matchMedia('(max-width: 1023px)').matches : window.innerWidth < 1024;
    const [mobile, setMobile] = useState(matches);
    useEffect(() => {
        const update = () => setMobile(matches());
        window.addEventListener('resize', update);
        return () => window.removeEventListener('resize', update);
    }, []);
    return mobile;
}
function Failure({ children, onRetry }) {
    return <div className="aed-error" role="alert"><span>{children}</span><button type="button" onClick={onRetry}>다시 시도</button></div>;
}
function EarningsDetail({ period, onClose, navigate, demo }) {
    const [selection] = useState({ period });
    const { earnings, refresh } = useAgentEarnings(selection, false, navigate, demo);
    const [unit, setUnit] = useState('day');
    const [selected, setSelected] = useState(null);
    const dialog = useRef(null);
    const data = earnings.data;
    const points = unit === 'week' ? weeklyTrend(visibleTrend(data)) : visibleTrend(data);
    useEffect(() => {
        const el = dialog.current;
        el.showModal();
        const overflow = document.body.style.overflow;
        document.body.style.overflow = 'hidden';
        return () => { el.close(); document.body.style.overflow = overflow; };
    }, []);
    useEffect(() => setSelected(null), [data, unit]);
    const chosen = points[selected];
    const peak = points.reduce((best, point) => !best || point.earnings > best.earnings ? point : best, null);
    const average = points.length ? Math.round(points.reduce((sum, point) => sum + point.earnings, 0) / points.length) : null;
    const options = {
        chart: { toolbar: { show: false }, animations: { enabled: false }, fontFamily: 'inherit', events: { dataPointSelection: (_event, _chart, config) => setSelected(config.dataPointIndex) } },
        colors: ['#3182f6'], plotOptions: { bar: { borderRadius: 4, columnWidth: '52%' } }, dataLabels: { enabled: false },
        xaxis: { categories: points.map(p => p.label), axisBorder: { show: false }, axisTicks: { show: false }, labels: { rotate: -45, hideOverlappingLabels: true, style: { colors: '#6b7684', fontSize: '11px' } } },
        yaxis: { labels: { formatter: value => Math.round(value).toLocaleString('ko-KR'), style: { colors: '#6b7684' } } },
        grid: { borderColor: '#eef0f3' }, tooltip: { y: { formatter: won } },
    };
    return <dialog ref={dialog} className="aed-detail" aria-labelledby="aed-detail-title" onCancel={event => { event.preventDefault(); onClose(); }} onClick={event => { if (event.target === dialog.current) onClose(); }}>
        <div className="aed-detail-content">
            <header><h2 id="aed-detail-title">{LABELS[period]} 수익 추세{demo && <span className="aed-demo" title="실제 수익이 아닌 예시 데이터입니다" aria-label="예시 데이터">예시</span>}</h2><button autoFocus type="button" aria-label="수익 상세 닫기" onClick={onClose}><X size={24} /></button></header>
            <p className="aed-muted">{data?.range.label || '수익을 조회하고 있습니다.'}</p>
            <strong className="aed-detail-total">{earnings.loading ? '조회 중…' : won(data?.summary.earnings)}</strong>
            <p className="aed-muted">발생 수익 · 한국시간 KST 기준</p>
            {earnings.error && <Failure onRetry={refresh}>{earnings.error}</Failure>}
            <div className="aed-trend-heading"><h3>{period === 'today' ? '시간별 수익' : unit === 'week' ? '주별 수익' : '일별 수익'}</h3>{period === 'month' && <div className="aed-units" role="group" aria-label="추세 단위">{['day', 'week'].map(value => <button type="button" key={value} aria-pressed={unit === value} onClick={() => setUnit(value)}>{value === 'day' ? '일별' : '주별'}</button>)}</div>}</div>
            {earnings.loading ? <p className="aed-placeholder" role="status">수익을 조회하고 있습니다.</p> : !data ? null : points.length === 0 ? <p className="aed-placeholder">해당 기간의 수익 내역이 없습니다.</p> : <>
                <p className="aed-selected" aria-live="polite">{chosen ? `${chosen.label} · ${won(chosen.earnings)}` : '막대를 누르면 해당 구간의 수익을 볼 수 있어요.'}</p>
                <Chart type="bar" height={220} options={options} series={[{ name: '발생 수익', data: points.map(p => p.earnings) }]} />
                <div className="aed-trend-summary"><div><span>가장 많이 번 구간</span><strong>{peak?.label}</strong><b>{won(peak?.earnings)}</b></div><div><span>표시 구간 평균</span><strong>{won(average)}</strong><small>{points.length}개 구간 기준</small></div></div>
                <details className="aed-chart-data"><summary>구간별 금액 보기</summary><ul>{points.map((point, index) => <li key={point.bucket_start}><button type="button" onClick={() => setSelected(index)}><span>{point.label}</span><strong>{won(point.earnings)}</strong></button></li>)}</ul></details>
            </>}
            {period === 'month' && unit === 'week' && <p className="aed-muted">월요일 기준으로 묶으며, 이번 달에 포함된 날짜만 합산합니다.</p>}
            {data && <footer className="aed-muted">{kstLabel(data.as_of)} 기준</footer>}
        </div>
    </dialog>;
}
export default function AgentDashboard() {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const location = useLocation();
    const mobile = useMobileTrendLayout();
    const user = useSelector(state => state.MyData);
    const walletEnabled = user?.wallet_use_yn === 'Y';
    const demo = isEarningsDemo(user);
    const { earnings, wallet, refresh } = useAgentEarnings(TODAY, walletEnabled, navigate, demo);
    const [desktopDetail, setDesktopDetail] = useState(null);
    const mobileDetail = Object.prototype.hasOwnProperty.call(LABELS, location.state?.agentIncomeTrend) ? location.state.agentIncomeTrend : null;
    const detail = mobileDetail || desktopDetail;
    const origin = useRef({ scroll: 0, buttonLabel: null, pushed: false });
    const previousDetail = useRef(null);
    const resizeTransition = useRef(false);
    const [transferOpen, setTransferOpen] = useState(false);
    const data = earnings.data;
    const openDetail = (period, event) => {
        origin.current = { scroll: window.scrollY, buttonLabel: event.currentTarget.getAttribute('aria-label'), pushed: false };
        if (!mobile) { setDesktopDetail(period); return; }
        origin.current.pushed = true;
        const state = location.state && typeof location.state === 'object' ? location.state : {};
        navigate(location.pathname + location.search, { state: { ...state, agentIncomeTrend: period } });
        window.scrollTo(0, 0);
    };
    const closeDetail = () => {
        if (!mobileDetail) { setDesktopDetail(null); return; }
        if (origin.current.pushed) { navigate(-1); return; }
        const { agentIncomeTrend, ...state } = location.state || {};
        navigate(location.pathname + location.search, { replace: true, state });
    };
    useAgentMobilePageHeader(detail && mobile ? `${LABELS[detail]} 수익 추세` : '내 수익', detail && mobile ? closeDetail : null, '내 수익으로 돌아가기');
    useEffect(() => {
        // Preserve Android/browser back behavior if a desktop dialog becomes a mobile page after resize.
        if (mobile && desktopDetail && !mobileDetail && !resizeTransition.current) {
            resizeTransition.current = true;
            origin.current.pushed = true;
            const state = location.state && typeof location.state === 'object' ? location.state : {};
            navigate(location.pathname + location.search, { state: { ...state, agentIncomeTrend: desktopDetail } });
            window.scrollTo(0, 0);
        } else if (mobileDetail && desktopDetail) {
            setDesktopDetail(null);
            resizeTransition.current = false;
        } else if (!desktopDetail) resizeTransition.current = false;
    }, [mobile,desktopDetail,mobileDetail,location.pathname,location.search,location.state,navigate]);
    useEffect(() => {
        if (previousDetail.current && !detail && origin.current.buttonLabel) {
            const { scroll, buttonLabel } = origin.current;
            requestAnimationFrame(() => {
                window.scrollTo(0, scroll);
                const button = [...document.querySelectorAll('.agent-earnings-dashboard button')].find(item => item.getAttribute('aria-label') === buttonLabel);
                button?.focus({ preventScroll: true });
            });
            origin.current = { scroll: 0, buttonLabel: null, pushed: false };
        }
        previousDetail.current = detail;
    }, [detail]);
    useEffect(() => {
        dispatch(change_Current_Location('영업자 내 수익'));
        dispatch(changeHeaderTitle({ mainTitle: '내 수익', subTitle: '' }));
    }, [dispatch]);
    const amount = value => earnings.loading ? '조회 중…' : won(value);
    if (detail && mobile) return <MobileTrend key={detail} period={detail} demo={demo} navigate={navigate} />;
    return <main className={`agent-earnings-dashboard aed-split${walletEnabled ? " aed-has-wallet" : ""}`} aria-label="영업자 내 수익">
        <section className="aed-income" aria-label="수익 요약" aria-busy={earnings.loading}>
            <div className="aed-hero">
                <h1>오늘 벌었어요{demo && <span className="aed-demo" title="수익은 예시 데이터이며 지갑 잔액은 실제 조회값입니다" aria-label="예시 데이터">예시</span>}</h1><div className="aed-hero-value"><strong data-testid="earnings-value">{amount(data?.summary.earnings)}</strong></div>
            </div>
            {data?.comparison && <p className={`aed-comparison${data.comparison.difference < 0 ? ' aed-negative' : ''}`}>{data.comparison.label} <b>{data.comparison.difference > 0 ? '+' : ''}{won(data.comparison.difference)}</b></p>}
            <div className="aed-actions"><div className="aed-updated"><span>{data ? `${kstLabel(data.as_of)} 기준` : '수익 정보를 조회합니다.'}</span><button type="button" aria-label="새로고침" onClick={refresh} disabled={earnings.loading}><RefreshCw size={19} /></button></div><button type="button" className="aed-trend-button" aria-label="오늘 수익 추세 보기" onClick={event => openDetail('today',event)}><TrendingUp size={20} />수익 추세<ChevronRight size={18} /></button></div>
            {earnings.error && <Failure onRetry={refresh}>{earnings.error}</Failure>}
            <div className="aed-totals">{['week', 'month'].map(period => <button type="button" key={period} aria-label={`${LABELS[period]} 수익 추세 보기`} onClick={event => openDetail(period,event)}><span>{LABELS[period]}</span><strong>{amount(data?.totals[`${period}_earnings`])}</strong><ChevronRight size={21} /></button>)}</div>
            
        </section>
        {walletEnabled && <section className="aed-wallet" aria-label="내 지갑" aria-busy={wallet.loading}>
            <h2><Wallet size={25} />내 지갑</h2>
            {wallet.error ? <Failure onRetry={refresh}>{wallet.error}</Failure> : <div className="aed-wallet-body"><div><p>출금 가능 잔액</p><strong>{wallet.loading ? '조회 중…' : won(wallet.data?.available)}</strong></div><button type="button" className="aed-send" disabled={!wallet.data || wallet.loading} onClick={() => setTransferOpen(true)}>송금</button></div>}
            <button type="button" className="aed-history" onClick={() => navigate('/agentWallet')}>입출금 내역<ChevronRight size={16} /></button>
        </section>}
        <footer className="aed-footnote">발생 수익 기준 · 오늘은 KST 00시부터 집계합니다.{walletEnabled && <span>발생 수익과 출금 가능 잔액은 다를 수 있습니다.</span>}</footer>
        {transferOpen && walletEnabled && <AgentWalletTransfer user={user} balance={wallet.raw} onClose={()=>setTransferOpen(false)} onRefresh={refresh} onHistory={()=>{setTransferOpen(false);navigate('/agentWallet');}} />}
        {detail && <EarningsDetail key={detail} period={detail} demo={demo} onClose={closeDetail} navigate={navigate} />}
    </main>;
}
